import { Theme } from "@mui/material";
import { documentCardGradient } from "theme/colors";
import {
  dropzoneFont,
  dropzoneRef,
} from "global/components/CustomDropzone/CustomDropzone.tokens";

/**
 * Centralized styles for the mobile sign-request flow. Every color is read from
 * theme.palette.mobile (see src/theme/colors.ts) so nothing is hardcoded.
 */
export const MobileSignRequestStyles = (theme: Theme) => {
  const m = theme.palette.mobile;
  // Exact Figma gradients.
  // Full-strength "Primary Gradient" (orange → coral) for gradient CTAs/accents.
  const primaryGradient = `linear-gradient(92deg, ${m.orange[500]} 1.95%, ${m.coral[500]} 97.4%)`;
  // Light "Primary Gradient-50" (peach → blush) for tinted backgrounds
  // (field-chip icon squares, etc.).
  const primaryGradient50 = `linear-gradient(92deg, ${m.peach[100]} 1.95%, ${m.peach[200]} 97.4%)`;
  // Same light gradient at the stepper (~92°) and template-row (~100°) angles.
  const stepperGradient = `linear-gradient(92.19deg, ${m.peach[100]} 1.95%, ${m.peach[200]} 97.4%)`;
  const templateGradient = `linear-gradient(99.78deg, ${m.peach[100]} 1.95%, ${m.peach[200]} 97.4%)`;
  // Uploaded-document card fill (lavender → sky → peach). Shared with desktop.
  const fileGradient = documentCardGradient;

  return {
    // Reusable gradients.
    primaryGradient,
    primaryGradient50,
    fileGradient,

    root: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      bgcolor: m.surface.white,
    },

    // Header (title row + stepper): plain white with a grey bottom border.
    header: {
      position: "sticky",
      top: 0,
      zIndex: 10,
      flexShrink: 0,
      background: m.surface.white,
      px: 2,
      pt: 2,
      pb: 1,
      borderBottom: `1px solid ${m.grey[50]}`,
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 2,
    },
    titleText: {
      fontSize: "1rem",
      fontWeight: 600,
      color: m.base[500],
    },
    headerIcon: {
      color: m.base[500],
      p: 0.5,
    },

    // Stepper — spans the full header width so the first step aligns under the
    // back arrow and the last step under the three-dot menu. The connectors
    // stretch to fill the space between the fixed-size circles.
    stepperRow: {
      display: "flex",
      alignItems: "flex-start",
      width: "100%",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
    // Each step is an equal-width column so the circles sit at evenly spaced
    // centers regardless of how wide the label ("Reminders") is.
    stepItem: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
    },
    // Connector runs from the previous circle's centre to this one's, drawn
    // behind the circles (left: -50% + width: 100% = exactly centre-to-centre).
    stepConnector: (filled: boolean) => ({
      position: "absolute",
      top: "15px",
      left: "-50%",
      width: "100%",
      height: "2px",
      bgcolor: filled ? m.ePurple[500] : m.grey[50],
      zIndex: 0,
    }),
    stepCircle: (active: boolean, completed: boolean) => ({
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "0.75rem",
      fontWeight: 600,
      flexShrink: 0,
      position: "relative",
      zIndex: 1,
      transition: "all 0.2s ease",
      ...(active || completed
        ? { bgcolor: m.ePurple[500], color: m.surface.white, border: "none" }
        : {
            background: stepperGradient,
            color: m.base[900],
            border: "none",
          }),
    }),
    stepLabel: (active: boolean) => ({
      mt: 0.75,
      fontSize: "0.6875rem",
      fontWeight: active ? 600 : 500,
      whiteSpace: "nowrap",
      color: active ? m.base[500] : m.grey[400],
    }),

    // Body
    body: {
      flexGrow: 1,
      px: 2,
      pt: 2,
      pb: "120px", // clear the fixed bottom bar
    },

    // Select Document card
    card: {
      border: `1px solid ${m.surface[100]}`,
      borderRadius: "16px",
      p: 2,
      bgcolor: m.surface.white,
    },
    cardTitle: {
      fontSize: "1rem",
      fontWeight: 600,
      color: m.base[500],
      mb: 2,
    },
    /*
      Same surface as `DropzoneBody` / the Dashboard — dashed sand border, 16px
      radius, brand-orange tinted circle. Kept as styles rather than swapped for
      `CustomDropzone` so this step keeps its own tap handling and its
      `aria-invalid` error wiring.
    */
    dropzone: (error: boolean) => ({
      border: `2px dashed ${
        error ? theme.palette.error.main : dropzoneRef.border
      }`,
      borderRadius: "16px",
      py: 4,
      px: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      cursor: "pointer",
      backgroundColor: theme.palette.background.paper,
      transition: "background-color 0.2s ease, border-color 0.15s ease",
      "&:active": { bgcolor: dropzoneRef.hoverBg },
    }),
    dropzoneIconWrap: {
      width: 44,
      height: 44,
      borderRadius: "50%",
      bgcolor: dropzoneRef.accentTint,
      color: dropzoneRef.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mb: 1.25,
    },
    dropzoneTitle: {
      fontSize: dropzoneFont.title.xs,
      fontWeight: 800,
      color: dropzoneRef.heading,
      mb: 0.625,
    },
    dropzoneHint: {
      fontSize: dropzoneFont.sub.xs,
      fontWeight: 500,
      color: dropzoneRef.metaText,
      lineHeight: 1.5,
    },
    orRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      my: 2,
    },
    orText: {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: m.grey[700],
      letterSpacing: "0.5px",
    },
    templateRow: {
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      background: templateGradient,
      borderRadius: "12px",
      p: 1.5,
      cursor: "pointer",
    },
    templateIconWrap: {
      width: 40,
      height: 40,
      borderRadius: "10px",
      bgcolor: m.surface.white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    templateTitle: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: m.base[500],
    },
    templateSubtitle: {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: m.grey[400],
    },

    // Uploaded documents
    uploadedLabel: {
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.5px",
      color: m.grey[400],
      textTransform: "uppercase",
      mt: 3,
      mb: 1.5,
    },
    fileRow: {
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      borderRadius: "12px",
      p: 1.5,
      mb: 1.5,
    },
    fileIconWrap: {
      width: 44,
      height: 44,
      borderRadius: "10px",
      bgcolor: m.red[50],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    fileIconText: {
      fontSize: "0.625rem",
      fontWeight: 700,
      color: m.red[700],
    },
    fileName: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: m.base[500],
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fileSize: {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: m.grey[400],
    },
    fileRemove: {
      color: m.grey[400],
      p: 0.5,
    },

    // Bottom bar
    bottomBar: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1.5,
      bgcolor: m.surface.white,
      borderTop: `1px solid ${m.surface[100]}`,
      boxShadow: "0px -4px 12px rgba(0,0,0,0.06)",
    },
    addAction: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      color: m.base[500],
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
      cursor: "pointer",
      "&:hover": { bgcolor: "transparent" },
    },
    continueButton: {
      bgcolor: m.ePurple[500],
      color: m.surface.white,
      textTransform: "none",
      fontSize: "0.875rem",
      fontWeight: 600,
      borderRadius: "12px",
      px: 3,
      py: 1.25,
      boxShadow: "none",
      "&:hover": { bgcolor: m.ePurple[700], boxShadow: "none" },
      "&.Mui-disabled": {
        bgcolor: m.grey[100],
        color: m.surface.white,
      },
    },

    // ---- Step 2: Signers ----
    orderToggleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      // Break out of the body's 16px side padding so the divider spans edge-to-edge.
      mx: -2,
      px: 2,
      py: 1.5,
      borderBottom: `1px solid ${m.surface[100]}`,
    },
    orderToggleLabel: {
      fontSize: "0.875rem",
      fontWeight: 700,
      color: m.base[900],
    },
    tabsRow: {
      display: "flex",
      borderBottom: `1px solid ${m.surface[100]}`,
      // Full-width track, aligned with the divider above.
      mx: -2,
      px: 2,
      mb: 2,
    },
    tab: (active: boolean) => ({
      flex: 1,
      textAlign: "center",
      py: 1.5,
      fontSize: "0.9375rem",
      fontWeight: active ? 700 : 500,
      color: active ? m.base[500] : m.grey[400],
      cursor: "pointer",
      borderBottom: active
        ? `2px solid ${m.ePurple[500]}`
        : "2px solid transparent",
      mb: "-1px",
    }),
    signerCard: {
      border: `1px solid ${m.surface[100]}`,
      borderRadius: "16px",
      p: 1.5,
      mb: 1.5,
      bgcolor: m.surface.white,
    },
    signerCardHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 0,
    },
    signerCardHeadLeft: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    },
    signerBadge: (color: string) => ({
      width: 24,
      height: 24,
      borderRadius: "50%",
      bgcolor: color,
      color: m.surface.white,
      fontSize: "0.875rem",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }),
    signerCardTitle: {
      fontSize: "1rem",
      fontWeight: 600,
      color: m.base[500],
    },
    // Empty state for the Signers / Approvers tabs — a soft tinted icon badge
    // over a title + one-line subtitle, matching the rest of the mobile flow's
    // gradient-tinted iconography rather than desktop's plain dashed box.
    recipientsEmptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.75,
      border: `1px solid ${m.surface[100]}`,
      borderRadius: "18px",
      bgcolor: m.surface.white,
      p: "32px 24px",
      textAlign: "center",
    },
    recipientsEmptyStateIcon: {
      width: 56,
      height: 56,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: primaryGradient50,
      color: m.ePurple[500],
      mb: 0.5,
    },
    recipientsEmptyStateTitle: {
      fontSize: "0.9375rem",
      fontWeight: 700,
      color: m.base[900],
    },
    recipientsEmptyStateSubtitle: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: m.grey[400],
      lineHeight: 1.4,
      maxWidth: 240,
    },
    recipientsEmptyStateActions: {
      display: "flex",
      gap: 1,
      mt: 1.25,
      flexWrap: "wrap",
      justifyContent: "center",
    },
    fieldLabel: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.4px",
      color: m.grey[400],
      textTransform: "uppercase",
      // Required: some callers render this as `component="label"`, and an inline
      // <label> silently drops the vertical margins below — which is why those
      // fields used to sit tighter than the ones using a default (block) <p>.
      display: "block",
      mb: 0.5,
      // Sole source of the gap between stacked form fields — the fields are flat
      // siblings, so this margin is what separates one from the next. Kept in one
      // place so every field on a card is spaced identically.
      mt: 1,
    },
    textInput: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        fontSize: "0.875rem",
        fontWeight: 500,
        color: m.base[500],
        bgcolor: m.surface.white,
        "& fieldset": { borderColor: m.surface[100] },
        "&:hover fieldset": { borderColor: m.grey[50] },
        "&.Mui-focused fieldset": {
          borderColor: m.ePurple[500],
          borderWidth: "1px",
        },
        "&.Mui-error fieldset": { borderColor: theme.palette.error.main },
      },
      "& .MuiOutlinedInput-input": {
        padding: "15px 16px",
        "&::placeholder": { color: m.base[100], opacity: 1 },
      },
    },
    // Borderless white variant — used for fields inside tinted containers
    // (e.g. approver group member boxes).
    textInputFilled: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        fontSize: "0.875rem",
        fontWeight: 500,
        color: m.base[500],
        bgcolor: m.surface.white,
        "& fieldset": { border: "none" },
        "&:hover fieldset": { border: "none" },
        "&.Mui-focused fieldset": {
          border: `1px solid ${m.ePurple[500]}`,
        },
        "&.Mui-error fieldset": {
          border: `1px solid ${theme.palette.error.main}`,
        },
      },
      "& .MuiOutlinedInput-input": {
        padding: "15px 16px",
        "&::placeholder": { color: m.base[100], opacity: 1 },
      },
    },
    dropdownField: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: `1px solid ${m.surface[100]}`,
      borderRadius: "14px",
      px: 2,
      py: 1.75,
      cursor: "pointer",
      bgcolor: m.surface.white,
      minWidth: 0,
    },
    dropdownFieldText: (placeholder: boolean) => ({
      fontSize: "0.875rem",
      fontWeight: 500,
      color: placeholder ? m.base[100] : m.base[500],
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    addDelayLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      color: m.base[500],
      fontSize: "0.875rem",
      fontWeight: 700,
      cursor: "pointer",
    },
    delayChip: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      bgcolor: m.ePurple[50],
      borderRadius: "16px",
      px: 2,
      py: 1.5,
    },
    delayChipLabel: {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: m.grey[400],
      lineHeight: 1.2,
    },
    delayChipValue: {
      fontSize: "0.875rem",
      fontWeight: 700,
      color: m.base[500],
      mt: 0.5,
    },

    // ---- SMS delivery (deliver-via channels, delivery phone, phone auth) ----
    // Mirrors the desktop row's "Deliver via" line (see
    // `components/SignerInputFields.tsx`), restacked for a phone: the options
    // sit under the field label instead of beside it.
    deliverViaGroup: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 2,
    },
    deliverOption: {
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      p: 0,
      minWidth: 0,
      textTransform: "none",
      lineHeight: "normal",
      "&:hover": { bgcolor: "transparent" },
      "&.Mui-disabled": { opacity: 0.55 },
    },
    deliverBox: (on: boolean) => ({
      width: 18,
      height: 18,
      borderRadius: "5px",
      border: `1px solid ${on ? m.ePurple[500] : m.grey[50]}`,
      bgcolor: on ? m.ePurple[500] : m.surface.white,
      color: m.surface.white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      flexShrink: 0,
    }),
    deliverTick: (on: boolean) => ({
      width: 12,
      height: 12,
      opacity: on ? 1 : 0,
    }),
    deliverOptionLabel: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: m.base[500],
      lineHeight: "normal",
    },
    deliverOptionNote: {
      ml: "5px",
      fontSize: "0.6875rem",
      fontWeight: 500,
      color: m.grey[400],
      lineHeight: "normal",
    },
    deliverLock: {
      fontSize: "0.875rem",
      color: m.grey[400],
      ml: "2px",
      flexShrink: 0,
    },
    // Desktop puts the plan/admin reason in a hover tooltip; a phone has no
    // hover, so the same copy is spelled out under the locked channel.
    deliverGateNote: {
      display: "block",
      fontSize: "0.6875rem",
      fontWeight: 500,
      color: m.grey[400],
      mt: 0.75,
    },
    /** Shared `CustomContactNumberInput`, restyled to this card's field chrome. */
    phoneField: {
      "& .MuiOutlinedInput-root": {
        height: 52,
        borderRadius: "14px",
        bgcolor: m.surface.white,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: m.surface[100] },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: m.grey[50] },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: m.ePurple[500],
          borderWidth: "1px",
        },
      },
      "& .MuiOutlinedInput-input": {
        fontSize: "0.875rem",
        fontWeight: 500,
        color: m.base[500],
        "&::placeholder": { color: m.base[100], opacity: 1 },
      },
      // The row's own validation message is surfaced as a toast on Continue,
      // exactly as on desktop — keep the field height fixed.
      "& .MuiFormHelperText-root": { display: "none" },
    },
    /** Tinted phone-authentication block — desktop's warm `authRow`, stacked. */
    authBlock: {
      mt: 1,
      p: 1.5,
      borderRadius: "14px",
      bgcolor: m.ePurple[50],
    },
    authBlockHead: {
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mb: 0.5,
    },
    authTitle: {
      fontSize: "0.875rem",
      fontWeight: 700,
      color: m.base[500],
    },
    authNote: {
      display: "block",
      fontSize: "0.75rem",
      fontWeight: 500,
      color: m.grey[900],
      mb: 1,
    },
    sameAsDelivery: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      mt: 1,
      p: 0,
      minWidth: 0,
      textTransform: "none",
      fontSize: "0.75rem",
      fontWeight: 700,
      color: m.ePurple[500],
      lineHeight: "normal",
      "&:hover": { bgcolor: "transparent" },
    },

    // ---- Approvers ----
    approverMemberBox: (color: string) => ({
      bgcolor: `${color}14`, // group color at low alpha
      borderRadius: "12px",
      p: 1.5,
      mb: 1.5,
    }),
    approverMemberHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 0.5,
    },
    approverMemberLabel: {
      fontSize: "0.875rem",
      fontWeight: 700,
      color: m.base[500],
    },
  };
};
